import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getAccountStatements } from "../../../AsianExchangeSrc/api";
import Loader from "../../../AsianExchangeSrc/utils/Loader";
import { selectSite } from "../../../AsianExchangeSrc/utils/helper/commonSelectors";
import CommonTable from "../../components/TableComponent/TableComponent";

const ProfitLoss = () => {
  const [dataSource, setDataSource] = useState("");
  const [sport, setSport] = useState("ALL");
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBet, setSelectedBet] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const topHeaderColor = useSelector(selectSite())?.headerColor || {
    start: "#2E4B5E",
    end: "#243A48",
  };
  const [entriesPerPage, setEntriesPerPage] = useState("10");
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  

  useEffect(() => {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split("T")[0];

    if (dataSource === "Live Data") {
      setStartDate(formatDate(today));
      setEndDate(formatDate(today));
    } else if (dataSource === "Backup Data") {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      setStartDate(formatDate(threeMonthsAgo));
      setEndDate(formatDate(today));
    } else if (dataSource === "Old Data") {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(today.getFullYear() - 1);
      setStartDate(formatDate(oneYearAgo));
      setEndDate(formatDate(today));
    } else {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);
      setStartDate(formatDate(sevenDaysAgo));
      setEndDate(formatDate(today));
    }
  }, [dataSource]);

  const getProfitLossSummary = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAccountStatements({
        filterby: "settlement",
        page,
        limit: parseInt(entriesPerPage),
        startDate,
        endDate,
        sportId: sport === "ALL" ? undefined : sport,
      });
      setTransactions(
        res?.data?.transactions.map((transaction, index) => ({
          id: transaction._id || index,
          index: (page - 1) * parseInt(entriesPerPage) + index + 1,
          date: transaction.createdAt
            ? new Date(transaction.createdAt).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
              })
            : "--",
          sportName: transaction.remark?.includes("International")
            ? "Int Casino"
            : transaction.event || "N/A",
          profitLoss: transaction.deposit
            ? Number(transaction.deposit).toFixed(2)
            : transaction.withdraw
            ? `-${Number(transaction.withdraw).toFixed(2)}`
            : "-",
          commission: transaction.commission
            ? Number(transaction.commission).toFixed(2)
            : "__",
          balance: transaction.balance?.toFixed(2) || "0.00",
          betId: transaction.betId,
        }))
      );
      setTotalItems(res?.data?.total || 0);
      setTotalPages(Math.ceil(res?.data?.total / parseInt(entriesPerPage)) || 1);
    } catch (error) {
      setError("Failed to fetch profit/loss statements. Please try again.");
      console.error("Error fetching profit/loss statements:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProfitLossSummary(1);
  }, [startDate, endDate, sport]);

  const handleGetCommission = () => {
    getProfitLossSummary(1);
  };

  // Placeholder for getBets function (as it wasn't provided)
  const getBets = (betId) => {
    setSelectedBet(betId);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedBet(null);
  };

  const columns = [
    { key: "index", header: "No", mobileLabel: "No" },
    { key: "date", header: "Date", mobileLabel: "Date" },
    {
      key: "sportName",
      header: "Sport Name",
      mobileLabel: "Sport",
      renderCell: (row) => (
        <span className="text-[#2789ce] cursor-pointer">{row.sportName}</span>
      ),
    },
    {
      key: "profitLoss",
      header: "Profit/Loss",
      mobileLabel: "P/L",
      renderCell: (row) => (
        <span
          className={
            row.profitLoss.startsWith("-") ? "text-red-500" : "text-green-500"
          }
        >
          {row.profitLoss}
        </span>
      ),
    },
    { key: "commission", header: "Commission", mobileLabel: "Comm" },
    { key: "balance", header: "Balance", mobileLabel: "Balance" },
  ];

  return (
    <div className="mx-auto bg-light-bg dark:bg-dark-bg border-t-2 rounded-t-lg border-gray-900 mx-1">
      {/* Filter Section */}
      <div className="bg-light-bg dark:bg-dark-bg p-4 space-y-4 border-2 border-gray-300">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <select
              value={dataSource}
              onChange={(e) => setDataSource(e.target.value)}
              className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-gray-300 rounded focus:outline-none focus:border-blue-500 text-black dark:text-white"
            >
              <option value="">All Sources</option>
              <option value="Live Data">Live Data</option>
              <option value="Backup Data">Backup Data</option>
              <option value="Old Data">Old Data</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-gray-300 rounded focus:outline-none focus:border-blue-500 text-black dark:text-white pr-10"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
          <div className="relative">
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-gray-300 rounded focus:outline-none focus:border-blue-500 text-black dark:text-white pr-10"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
        </div>
        <div>
          <button
            onClick={handleGetCommission}
            disabled={loading}
            className={`w-full bg-[#6A7D88] hover:bg-slate-700 text-white font-medium py-2 px-4 rounded transition-colors ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            Get History
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-100 text-red-700 border border-red-300 rounded mt-4">
          {error}
        </div>
      )}

      {/* Loader */}
      {loading && <Loader />}

      {/* Profit/Loss Table */}
      {!loading && !error && (
        <CommonTable
          title="Profit Loss"
          data={transactions}
          columns={columns}
          showEntriesOptions={[10, 25, 50, 100]}
          initialEntriesPerPage={10}
          showPagination={true}
          emptyMessage="No statements found."
        />
      )}
    </div>
  );
};

export default ProfitLoss;